import React, { useState } from 'react';
import {Button, Dialog, DialogContent, IconButton} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {RootState} from "../../store";
import {getMembershipPlanLabel} from "../../util/membershipPlan";
import {formatYmdFromDate, formatYmdFromTimestamp} from "../../util/date";
import {MembershipQueueItem} from "../../api/payment";
import {ImmediateIcon} from "../../icons";
import {useMatomoTrackingPoint} from "../../hooks/useMatomoTrackingPoint";

type ExistingMembershipDialogProps = {
    open: boolean;
    isSameTier: boolean;
    currentTier?: string;
    callBackPlan?: { plan: string; amount: number; dropAmount?: number };
    renewalQueueMonths?: number;
    membershipQueue: MembershipQueueItem[];
    onClose: () => void;
    onConfirm: (immediateEffect?: boolean) => void;
};

const DATE_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

function formatYmd(year: number, month: number, day: number): string {
    const y = String(year);
    const m = String(month).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

function addMonthsWithMonthEndClamp(dateStr?: string | null, months = 0): string {
    if (!dateStr) return '';
    const match = DATE_REGEX.exec(dateStr);
    if (!match) return dateStr;

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);

    if (!year || !month || !day) return dateStr;

    const totalMonths = (year * 12 + (month - 1)) + months;
    const targetYear = Math.floor(totalMonths / 12);
    const targetMonthIndex = totalMonths % 12;
    const maxDayInTargetMonth = new Date(targetYear, targetMonthIndex + 1, 0).getDate();
    const targetDay = Math.min(day, maxDayInTargetMonth);

    return formatYmd(targetYear, targetMonthIndex + 1, targetDay);
}

function addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}

function formatWithThousandsSeparator(value: number): string {
    return new Intl.NumberFormat('en-US').format(value);
}

export default function ExistingMembershipDialog(props: ExistingMembershipDialogProps) {
    const {open, isSameTier, renewalQueueMonths, onClose, onConfirm, currentTier, callBackPlan, membershipQueue} = props;
    const {t} = useTranslation();
    const memberInfo = useSelector((state: RootState) => state.member.memberInfo);
    const {trackRenewalPlanChoose} = useMatomoTrackingPoint();
    const [selected, setSelected] = useState<'immediate' | 'expire' | null>(null);

    // 每次打开弹窗时重置选择
    React.useEffect(() => {
        if (!open) setSelected(null);
    }, [open]);

    const hasPending = (renewalQueueMonths ?? 0) > 0;
    const formattedCallbackDropAmount = formatWithThousandsSeparator(callBackPlan?.dropAmount ?? 0);
    const formattedMemberDrops = formatWithThousandsSeparator(memberInfo?.memberDrops ?? 0);
    const currentPlanLabel = getMembershipPlanLabel(currentTier ?? memberInfo?.membershipTier);
    const callbackPlanLabel = getMembershipPlanLabel(callBackPlan?.plan);
    const renewalMonths = callBackPlan?.plan?.includes('_year') ? 12 : 1;

    // 按队列逐项累加日期：月会员按月加，年会员按年(12个月)加
    const calculateEndDateFromQueue = (baseDate: string, queue: MembershipQueueItem[]): string => {
        let date = baseDate;
        for (const item of queue) {
            const months = item.tier?.includes('_year') ? 12 : (item.duration_months || 1);
            date = addMonthsWithMonthEndClamp(date, months);
        }
        return date;
    };

    const baseExpireDate = memberInfo?.currentMemberEndTime
        ? formatYmdFromDate(new Date(memberInfo.currentMemberEndTime))
        : '';

    // 队列项按是否同 tier 分组（line 1 只看当前 tier，line 2 水滴覆盖所有 tier）
    const currentTierString = currentTier ?? memberInfo?.membershipTier;
    const allQueuedItems = (membershipQueue ?? []).filter(item => item.status === 'queued');
    const sameTierQueue = allQueuedItems.filter(item => item.tier === currentTierString);

    const currentExpireDate = baseExpireDate
        ? calculateEndDateFromQueue(baseExpireDate, [])
        : '';
    const memberDropsExpiresAt = formatYmdFromTimestamp(
        memberInfo?.currentMemberEndTime,
        ''
    );
    // 当前 tier 含同 tier 队列的到期日（line 1「{currentTier} 权益延长至」只看当前 tier 的链，
    // 不含其他 tier 队列项；对应需求文档变量 {currentMemberAllExpireDate}）
    // 用 calculateEndDateFromQueue 逐条累加，避免月底求和后偏差
    const currentMemberAllExpireDate = baseExpireDate
        ? calculateEndDateFromQueue(baseExpireDate, sameTierQueue)
        : '';
    const nextMemberExpireDate = addMonthsWithMonthEndClamp(currentMemberAllExpireDate, renewalMonths);
    const safeRemainDays = currentExpireDate
        ? Math.max(0, Math.floor((new Date(currentExpireDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
        : 0;
    const memberDropsExpireBaseDate = memberDropsExpiresAt
        ? formatYmdFromDate(addDays(new Date(memberDropsExpiresAt), -safeRemainDays))
        : '';
    const now = new Date();
    const nowYmd = formatYmdFromDate(now);
    const nextDropsExpireDateFromNow = addMonthsWithMonthEndClamp(nowYmd, renewalMonths);
    const nextPlanDate = addDays(now, safeRemainDays);
    const nextPlanExpireFromNow = currentExpireDate;
    const highlightStyle = {color: 'var(--c-accent)', fontWeight: 700, margin: '0 4px'};

    // line 2「水滴延长至」：水滴覆盖所有 tier 队列
    // - 同等级续购：从 baseExpireDate 起，所有 tier 队列逐条累加，再加新购月数
    // - 升级：当前剩余时长被覆盖，从 today 起，所有 tier 队列逐条累加，再加新购月数
    const allChainEndForSameTier = baseExpireDate
        ? calculateEndDateFromQueue(baseExpireDate, allQueuedItems)
        : '';
    const nextDropsExpireDate = isSameTier
        ? addMonthsWithMonthEndClamp(allChainEndForSameTier, renewalMonths)
        : addMonthsWithMonthEndClamp(calculateEndDateFromQueue(nowYmd, allQueuedItems), renewalMonths);

    let summaryText: React.ReactNode = '';
    if (isSameTier) {
        summaryText = (
            <>
                您的<span style={highlightStyle}>{currentPlanLabel}</span>权益将延长至<span style={highlightStyle}>{nextMemberExpireDate}</span>。
                <br />
                您所有剩余的<span style={highlightStyle}>{formattedMemberDrops}</span>会员水滴将延长至<span style={{
                ...highlightStyle,
                color: hasPending ? 'var(--c-brand)' : 'var(--c-accent)'
            }}>{nextDropsExpireDate}</span>。
                <br />
                新一期的<span style={highlightStyle}>{formattedCallbackDropAmount}</span>水滴将于<span style={{...highlightStyle, color: 'var(--c-violet)'}}>{nextPlanExpireFromNow}</span>发放
            </>
        );
    } else {
        summaryText = (
            <>
                您将立即获得<span style={highlightStyle}>{callbackPlanLabel}</span>权益和<span style={highlightStyle}>{formattedCallbackDropAmount}</span>水滴。
                <br />
                您所有剩余的<span style={highlightStyle}>{formattedMemberDrops}</span>会员水滴将延长至<span style={highlightStyle}>{nextDropsExpireDate}</span>。
                <br />
                升级后您的<span style={highlightStyle}>{callbackPlanLabel}</span>权益有效期为<span style={{...highlightStyle, color: 'var(--c-brand)'}}>{nextDropsExpireDateFromNow}</span>
            </>
        );
    }

    const formattedDrop = formatWithThousandsSeparator(callBackPlan?.dropAmount ?? 0);

    // 套餐颜色映射（与 PaymentMainDialog 保持一致）
    const getPlanStyle = (tier?: string): React.CSSProperties => {
        const colors: Record<string, string> = {
            plus: 'var(--c-accent-light)', plus_year: 'var(--c-accent-light)',
            pro: 'var(--c-brand-light)', pro_year: 'var(--c-brand-light)',
            max: 'linear-gradient(to right, var(--c-grad-start), var(--c-brand-light), var(--c-grad-end))', max_year: 'linear-gradient(to right, var(--c-grad-start), var(--c-brand-light), var(--c-grad-end))',
        };
        const c = colors[tier ?? ''];
        if (!c) return { color: 'var(--c-text-primary)' };
        if (c.startsWith('linear-gradient')) {
            return { background: c, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' };
        }
        return { color: c };
    };
    const planStyle = getPlanStyle(callBackPlan?.plan);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            sx={{
                '& .MuiDialog-paper': {
                    maxWidth: 'none',
                    borderRadius: '20px',
                    ...(isSameTier ? { width: '510px', margin: 0 } : {}),
                },
                '& .MuiDialogContent-root': {
                    padding: '0',
                }
            }}
        >
            <DialogContent>
                {isSameTier ? (
                    /* ---- 续费方式选择（Figma 设计） ---- */
                    <div className="renewal-dialog">
                        <div className="renewal-header">
                            <span className="renewal-title">请选择续购生效方式</span>
                            <IconButton onClick={onClose} sx={{padding: '10px'}}>
                                <CloseIcon />
                            </IconButton>
                        </div>

                        <div className="renewal-info">
                            <div className="renewal-info-row">
                                <span className="renewal-info-label">本次续购：</span>
                                <span className="renewal-info-value" style={planStyle}>{callbackPlanLabel}</span>
                            </div>
                            <div className="renewal-info-row">
                                <span className="renewal-info-label">获得会员水滴：</span>
                                <span className="renewal-info-value renewal-info-value--purple">{formattedDrop}</span>
                            </div>
                        </div>

                        <div className="renewal-options">
                            <div
                                className={`renewal-option-card ${selected === 'immediate' ? 'renewal-option-card--selected' : ''}`}
                                onClick={() => {
                                    setSelected('immediate');
                                    trackRenewalPlanChoose({ page_id: window.location.href, select_content: '立即生效' });
                                }}
                            >
                                <div className="renewal-option-radio">
                                    {selected === 'immediate' && <span className="renewal-radio-check">✓</span>}
                                </div>
                                <div className="renewal-option-content">
                                    <div className="renewal-option-title">
                                        <ImmediateIcon />
                                        <span>立即生效</span>
                                    </div>
                                    <div className="renewal-option-desc">
                                        立即发放 {formattedDrop} 水滴，有效期至 {nextDropsExpireDateFromNow}；当前会员周期剩余有效期不再顺延，待生效会员不受影响
                                    </div>
                                </div>
                            </div>

                            <div
                                className={`renewal-option-card ${selected === 'expire' ? 'renewal-option-card--selected' : ''}`}
                                onClick={() => {
                                    setSelected('expire');
                                    trackRenewalPlanChoose({ page_id: window.location.href, select_content: '到期生效' });
                                }}
                            >
                                <div className="renewal-option-radio">
                                    {selected === 'expire' && <span className="renewal-radio-check">✓</span>}
                                </div>
                                <div className="renewal-option-content">
                                    <div className="renewal-option-title">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path
                                                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"
                                                fill="var(--c-text-primary)"/>
                                        </svg>
                                        <span>到期生效</span>
                                    </div>
                                    <div className="renewal-option-desc">
                                        当前周期结束后生效，权益延长至 {nextMemberExpireDate}；新一期水滴于下期会员生效时发放
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="renewal-footer">
                            <Button variant="outlined" onClick={onClose} sx={{
                                textTransform: 'none', fontWeight: 700, color: 'var(--c-text-primary)',
                                borderRadius: '20px', border: '1px solid var(--c-text-primary)',
                                minWidth: '92px', height: '32px', fontSize: '16px',
                            }}>取消</Button>
                            <Button variant="contained" disabled={!selected} onClick={() => onConfirm(selected === 'immediate')} sx={{
                                textTransform: 'none', fontWeight: 700, color: 'var(--c-on-color)',
                                backgroundColor: 'var(--c-brand-light)', borderRadius: '20px',
                                minWidth: '139px', height: '32px', fontSize: '16px',
                                '&:hover': { backgroundColor: 'var(--c-brand-light-hover)' },
                                '&.Mui-disabled': { backgroundColor: 'var(--c-border)', color: 'var(--c-text-secondary)' }
                            }}>确认并支付</Button>
                        </div>
                    </div>
                ) : (
                    /* ---- 升级摘要（原有逻辑） ---- */
                    <div className='check-box' style={{fontWeight: 500, padding: 20}}>
                        <div style={{width: '430px', lineHeight: '30px'}}>{summaryText}</div>
                        <div style={{display: 'flex', gap: '16px', justifyContent: 'flex-end', marginTop: '20px'}}>
                            <Button variant="outlined" onClick={onClose} sx={{
                                minWidth: '100px', textTransform: 'none', fontWeight: 500,
                                color: 'var(--c-text-primary)', borderRadius: '20px', border: '1px solid var(--c-text-primary)'
                            }}>{t('cancel')}</Button>
                            <Button variant="contained" color="primary" onClick={() => onConfirm()} sx={{
                                minWidth: '100px', textTransform: 'none', fontWeight: 500,
                                color: 'var(--c-on-color)', backgroundColor: 'var(--c-brand-light)', borderRadius: '20px'
                            }}>{t('confirm_and_pay')}</Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
